import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { calendarDate } from '@/lib/dateOnly';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, Factory, Edit, Trash2, Package, Check } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';

interface Manufacturer {
  id: string;
  organisation_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  created_by_email: string | null;
  po_count?: number;
}

interface PurchaseOrderSummary {
  id: string;
  po_number: string;
  purchase_date: string | null;
  manufacturer_name: string | null;
  vendor_name: string | null;
  file_names: string[];
}

interface OrganisationManufacturersTabProps {
  organisation: {
    id: string;
    name: string;
  };
}

const OrganisationManufacturersTab: React.FC<OrganisationManufacturersTabProps> = ({
  organisation,
}) => {
  const { toast } = useToast();
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingManufacturer, setEditingManufacturer] = useState<Manufacturer | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [manufacturerName, setManufacturerName] = useState('');
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [manufacturerToDelete, setManufacturerToDelete] = useState<Manufacturer | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // PO assignment state
  const [availablePOs, setAvailablePOs] = useState<PurchaseOrderSummary[]>([]);
  const [assignedPOs, setAssignedPOs] = useState<PurchaseOrderSummary[]>([]);
  const [selectedPOsToAssign, setSelectedPOsToAssign] = useState<string[]>([]);
  const [selectedPOsToUnassign, setSelectedPOsToUnassign] = useState<string[]>([]);
  const [loadingPOs, setLoadingPOs] = useState(false);

  const fetchManufacturers = async () => {
    try {
      setLoading(true);
      
      // Fetch manufacturers
      const { data: manufacturersData, error: manufacturersError } = await supabase
        .from('manufacturers')
        .select('*')
        .eq('organisation_id', organisation.id)
        .order('name', { ascending: true });

      if (manufacturersError) throw manufacturersError;

      // Fetch PO counts by manufacturer_name (purchase_orders table)
      const { data: poData, error: poError } = await supabase
        .from('purchase_orders')
        .select('manufacturer_name, vendor_name')
        .eq('organisation_id', organisation.id);

      if (poError) throw poError;

      // Count POs per manufacturer name (case-insensitive)
      const poCounts: Record<string, number> = {};
      
      poData?.forEach((po) => {
        // Use manufacturer_name first, fallback to vendor_name
        const mfrName = po.manufacturer_name || po.vendor_name;
        if (mfrName) {
          const name = mfrName.toLowerCase().trim();
          poCounts[name] = (poCounts[name] || 0) + 1;
        }
      });

      // Merge PO counts with manufacturers (case-insensitive match)
      const manufacturersWithCounts = (manufacturersData || []).map((m) => ({
        ...m,
        po_count: poCounts[m.name.toLowerCase().trim()] || 0,
      }));

      setManufacturers(manufacturersWithCounts);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load manufacturers',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManufacturers();
  }, [organisation.id]);

  const resetForm = () => {
    setEditingManufacturer(null);
    setManufacturerName('');
    setAvailablePOs([]);
    setAssignedPOs([]);
    setSelectedPOsToAssign([]);
    setSelectedPOsToUnassign([]);
  };

  const fetchPOsForManufacturer = async (manufacturerName?: string) => {
    setLoadingPOs(true);
    try {
      // Fetch all POs for this org with their files
      const { data: allPOs, error } = await supabase
        .from('purchase_orders')
        .select(`
          id, 
          po_number, 
          purchase_date, 
          manufacturer_name, 
          vendor_name,
          purchase_order_files (file_name)
        `)
        .eq('organisation_id', organisation.id)
        .order('purchase_date', { ascending: false, nullsFirst: false });

      if (error) throw error;

      // Transform to include file_names array
      const pos: PurchaseOrderSummary[] = (allPOs || []).map(po => ({
        id: po.id,
        po_number: po.po_number,
        purchase_date: po.purchase_date,
        manufacturer_name: po.manufacturer_name,
        vendor_name: po.vendor_name,
        file_names: (po.purchase_order_files || []).map((f: { file_name: string }) => f.file_name),
      }));
      
      if (manufacturerName) {
        // Edit mode: separate assigned vs available
        const assigned = pos.filter(po => {
          const mfrName = po.manufacturer_name || po.vendor_name;
          return mfrName && mfrName.toLowerCase().trim() === manufacturerName.toLowerCase().trim();
        });

        // Available = all POs NOT assigned to this manufacturer
        const available = pos.filter(po => {
          const mfrName = po.manufacturer_name || po.vendor_name;
          return !mfrName || mfrName.toLowerCase().trim() !== manufacturerName.toLowerCase().trim();
        });

        setAssignedPOs(assigned);
        setAvailablePOs(available);
      } else {
        // Create mode: all POs are available for assignment
        setAssignedPOs([]);
        setAvailablePOs(pos);
      }
    } catch (error) {
      console.error('Error fetching POs:', error);
    } finally {
      setLoadingPOs(false);
    }
  };

  const handleOpenDialog = async (manufacturer?: Manufacturer) => {
    if (manufacturer) {
      setEditingManufacturer(manufacturer);
      setManufacturerName(manufacturer.name);
      fetchPOsForManufacturer(manufacturer.name);
    } else {
      resetForm();
      // Also fetch POs for create mode
      fetchPOsForManufacturer();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!manufacturerName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a manufacturer name',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (editingManufacturer) {
        // Update existing manufacturer
        const { error } = await supabase
          .from('manufacturers')
          .update({ name: manufacturerName.trim() })
          .eq('id', editingManufacturer.id);

        if (error) {
          if (error.code === '23505') {
            throw new Error('A manufacturer with this name already exists');
          }
          throw error;
        }

        // Assign selected POs to this manufacturer
        if (selectedPOsToAssign.length > 0) {
          const { error: assignError } = await supabase
            .from('purchase_orders')
            .update({ 
              manufacturer_name: manufacturerName.trim(),
              vendor_name: manufacturerName.trim()
            })
            .in('id', selectedPOsToAssign);

          if (assignError) throw assignError;
        }

        // Unassign selected POs (clear manufacturer)
        if (selectedPOsToUnassign.length > 0) {
          const { error: unassignError } = await supabase
            .from('purchase_orders')
            .update({ manufacturer_name: null })
            .in('id', selectedPOsToUnassign);

          if (unassignError) throw unassignError;
        }

        const changeCount = selectedPOsToAssign.length + selectedPOsToUnassign.length;
        toast({ 
          title: 'Success', 
          description: changeCount > 0 
            ? `Manufacturer updated and ${changeCount} PO(s) modified` 
            : 'Manufacturer updated' 
        });
      } else {
        // Create new manufacturer
        const { error } = await supabase
          .from('manufacturers')
          .insert({
            organisation_id: organisation.id,
            name: manufacturerName.trim(),
            created_by_email: user?.email || null,
          });

        if (error) {
          if (error.code === '23505') {
            throw new Error('A manufacturer with this name already exists');
          }
          throw error;
        }

        // Assign selected POs to this new manufacturer
        if (selectedPOsToAssign.length > 0) {
          const { error: assignError } = await supabase
            .from('purchase_orders')
            .update({ 
              manufacturer_name: manufacturerName.trim(),
              vendor_name: manufacturerName.trim()
            })
            .in('id', selectedPOsToAssign);

          if (assignError) throw assignError;
        }

        toast({ 
          title: 'Success', 
          description: selectedPOsToAssign.length > 0 
            ? `Manufacturer created and ${selectedPOsToAssign.length} PO(s) assigned`
            : 'Manufacturer created' 
        });
      }

      handleCloseDialog();
      fetchManufacturers();
    } catch (error) {
      console.error('Error saving manufacturer:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save manufacturer',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const togglePOToAssign = (poId: string) => {
    setSelectedPOsToAssign(prev => 
      prev.includes(poId) ? prev.filter(id => id !== poId) : [...prev, poId]
    );
  };

  const togglePOToUnassign = (poId: string) => {
    setSelectedPOsToUnassign(prev => 
      prev.includes(poId) ? prev.filter(id => id !== poId) : [...prev, poId]
    );
  };

  const handleDelete = async (manufacturer: Manufacturer) => {
    if (manufacturer.po_count && manufacturer.po_count > 0) {
      toast({
        title: 'Cannot Delete',
        description: `This manufacturer has ${manufacturer.po_count} purchase order(s) linked. Please reassign or delete them first.`,
        variant: 'destructive',
      });
      return;
    }

    setManufacturerToDelete(manufacturer);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!manufacturerToDelete) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from('manufacturers')
        .delete()
        .eq('id', manufacturerToDelete.id);

      if (error) throw error;
      toast({ title: 'Success', description: 'Manufacturer deleted' });
      fetchManufacturers();
    } catch (error) {
      console.error('Error deleting manufacturer:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete manufacturer',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setManufacturerToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Factory className="h-5 w-5" />
          Manufacturers
        </CardTitle>
        <Button onClick={() => handleOpenDialog()} data-testid="create-manufacturer-button">
          <Plus className="h-4 w-4 mr-2" />
          Add Manufacturer
        </Button>
      </CardHeader>
      <CardContent>
        {manufacturers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Factory className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No manufacturers found</p>
            <p className="text-sm mt-1">Add your first manufacturer to get started</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Purchase Orders</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {manufacturers.map((manufacturer) => (
                <TableRow key={manufacturer.id} data-testid="manufacturer-row">
                  <TableCell className="font-medium">{manufacturer.name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span>{manufacturer.po_count || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {format(new Date(manufacturer.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenDialog(manufacturer)}
                        className="inline-flex items-center justify-center text-primary hover:text-primary/80"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      {manufacturer.po_count && manufacturer.po_count > 0 ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center text-destructive/50 cursor-not-allowed"
                                disabled
                              >
                                <Trash2 size={16} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Cannot delete: {manufacturer.po_count} purchase order{manufacturer.po_count > 1 ? 's' : ''} linked</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDelete(manufacturer)}
                          className="inline-flex items-center justify-center text-destructive hover:text-destructive/80"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingManufacturer ? 'Edit Manufacturer' : 'Add Manufacturer'}
            </DialogTitle>
            <DialogDescription>
              {editingManufacturer
                ? 'Update the manufacturer name and manage assigned purchase orders.'
                : 'Enter a name for the new manufacturer and optionally assign purchase orders.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="manufacturer-name">Manufacturer Name</Label>
              <Input
                id="manufacturer-name"
                value={manufacturerName}
                onChange={(e) => setManufacturerName(e.target.value)}
                placeholder="Enter manufacturer name"
                disabled={submitting}
                autoFocus
              />
            </div>

            {/* PO Assignment Section */}
            <div className="space-y-4">
              {loadingPOs ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Currently Assigned POs - Only in edit mode */}
                  {editingManufacturer && assignedPOs.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        Assigned Purchase Orders ({assignedPOs.length})
                      </Label>
                      <ScrollArea className="h-[120px] border rounded-md p-2">
                        <div className="space-y-2">
                          {assignedPOs.map((po) => (
                            <div 
                              key={po.id} 
                              className="flex items-center gap-2 p-2 rounded hover:bg-muted/50"
                            >
                              <Checkbox
                                id={`unassign-${po.id}`}
                                checked={true}
                                disabled={true}
                              />
                              <label 
                                htmlFor={`unassign-${po.id}`}
                                className="flex-1 text-sm"
                              >
                                <div className="font-medium truncate">
                                  {po.file_names.length > 0 
                                    ? po.file_names.join(', ')
                                    : po.po_number}
                                </div>
                                {po.purchase_date && (
                                  <span className="text-muted-foreground text-xs">
                                    {format(calendarDate(po.purchase_date), 'MMM d, yyyy')}
                                  </span>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  )}

                  {/* Available POs to Assign */}
                  {availablePOs.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        {editingManufacturer ? 'Other Purchase Orders' : 'Purchase Orders'} ({availablePOs.length})
                      </Label>
                      <ScrollArea className="h-[150px] border rounded-md p-2">
                        <div className="space-y-2">
                          {availablePOs.map((po) => {
                            const currentMfr = po.manufacturer_name || po.vendor_name;
                            return (
                              <div 
                                key={po.id} 
                                className="flex items-center gap-2 p-2 rounded hover:bg-muted/50"
                              >
                                <Checkbox
                                  id={`assign-${po.id}`}
                                  checked={selectedPOsToAssign.includes(po.id)}
                                  onCheckedChange={() => togglePOToAssign(po.id)}
                                />
                                <label 
                                  htmlFor={`assign-${po.id}`}
                                  className="flex-1 text-sm cursor-pointer"
                                >
                                  <div className="font-medium truncate">
                                    {po.file_names.length > 0 
                                      ? po.file_names.join(', ')
                                      : po.po_number}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {po.purchase_date && format(calendarDate(po.purchase_date), 'MMM d, yyyy')}
                                    {currentMfr && <span className="ml-2">(currently: {currentMfr})</span>}
                                  </div>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                      {selectedPOsToAssign.length > 0 && (
                        <p className="text-xs text-primary">
                          <Check className="h-3 w-3 inline mr-1" />
                          {selectedPOsToAssign.length} PO(s) will be assigned
                        </p>
                      )}
                    </div>
                  )}

                  {assignedPOs.length === 0 && availablePOs.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      No purchase orders available
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || !manufacturerName.trim()}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingManufacturer ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Manufacturer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this manufacturer? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default OrganisationManufacturersTab;
